import type { DocMeta } from '@blocksuite/affine/store';
import { Map as YMap } from 'yjs';

/**
 * The page list (`meta.pages`) is a CRDT array, so the same doc id can appear
 * in it more than once. Every entry with an id describes the same doc: the
 * first entry is the doc's record, and the doc counts as trashed when any of
 * its entries is trashed.
 */
export type DocMetaEntry = YMap<unknown>;

/** Entries grouped by doc id, each group in page-list order. */
export function groupDocMetaEntries(
  pages: Iterable<unknown>
): Map<string, DocMetaEntry[]> {
  const groups = new Map<string, DocMetaEntry[]>();
  for (const page of pages) {
    if (!(page instanceof YMap)) continue;
    const id = page.get('id');
    if (typeof id !== 'string') continue;
    const group = groups.get(id);
    if (group) {
      group.push(page);
    } else {
      groups.set(id, [page]);
    }
  }
  return groups;
}

/** Each doc id once, in page-list order, with its first entry (its record). */
export function firstDocMetaEntries(
  pages: Iterable<unknown>
): [id: string, entry: DocMetaEntry][] {
  return [...groupDocMetaEntries(pages)].map(([id, [first]]) => [id, first]);
}

export function isTrashedEntry(entry: DocMetaEntry) {
  return Boolean(entry.get('trash'));
}

/** The earliest trash date among the trashed entries, if any has one. */
export function earliestTrashDate(
  entries: readonly DocMetaEntry[]
): number | undefined {
  let earliest: number | undefined;
  for (const entry of entries) {
    const trashDate = entry.get('trashDate');
    if (
      isTrashedEntry(entry) &&
      typeof trashDate === 'number' &&
      (earliest === undefined || trashDate < earliest)
    ) {
      earliest = trashDate;
    }
  }
  return earliest;
}

/** The doc's meta: its first entry, trashed when any entry is trashed. */
export function mergeDocMetaEntries(
  entries: readonly DocMetaEntry[]
): Partial<DocMeta> {
  const [first] = entries;
  if (!first) return {};
  const meta = first.toJSON() as Partial<DocMeta>;
  if (meta.trash || !entries.some(isTrashedEntry)) return meta;
  return { ...meta, trash: true, trashDate: earliestTrashDate(entries) };
}
